import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useId, useState } from "react";
import { ApiError } from "../lib/api";
import {
  acceptProposal,
  type CreateEntryInput,
  fetchFrise,
  fetchMembers,
  fetchProposals,
  type InvitedRole,
  inviteMember,
  type Member,
  type Proposal,
  queryKeys,
  refuseProposal,
  revokeMember,
  type Track,
} from "./api";
import { AppPage } from "./app-nav";
import { whenLabel } from "./dates";
import type { EntryForm } from "./entry-body";
import { EntrySheet } from "./entry-form";
import { SessionGate } from "./session-gate";
import { ShareLinks } from "./share-links";

const FIELD =
  "min-h-11 w-full rounded-[10px] border border-input bg-tl-field px-3 text-base text-foreground placeholder:text-[#8C96AA]";

export const ROLE_NAMES: Record<InvitedRole, string> = {
  family: "Famille",
  close: "Proche choisi",
};

/**
 * Mes proches (`/proches`, proprietaire seul ; spec, section 1, ecran 5 ; planche « Proches » ;
 * ticket 7) : les propositions a valider (Ajouter, Modifier puis ajouter, Refuser), l'invitation
 * d'une adresse avec son cercle, et les proches qui voient la frise, chacun avec « Retirer »,
 * puis les liens a envoyer (#588, `share-links.tsx`).
 * Un proche qui ouvre cette page recoit 403 `owner_only` du serveur : `SessionGate` le dit.
 */
export function ProchesPage() {
  const members = useQuery({ queryKey: queryKeys.members, queryFn: fetchMembers, retry: false });
  if (members.isPending) {
    return (
      <p className="text-muted-foreground" role="status">
        Chargement...
      </p>
    );
  }
  if (members.isError) {
    return <SessionGate error={members.error} retry={() => void members.refetch()} />;
  }
  return (
    <AppPage>
      <h1 className="tl-display m-0 text-[28px]">Mes proches</h1>
      <PendingProposals />
      <InviteForm />
      <MemberList members={members.data.members} />
      <ShareLinks />
    </AppPage>
  );
}

const asError = (error: unknown): ApiError =>
  error instanceof ApiError
    ? error
    : new ApiError("network", "La demande n'a pas abouti. Réessaie.", 0);

/** Le formulaire de la feuille, prerempli depuis une proposition (prive par defaut). */
function formOfProposal(proposal: Proposal): Partial<EntryForm> {
  return {
    kind: proposal.kind,
    title: proposal.title,
    trackId: proposal.trackId,
    startRaw: proposal.undated ? "" : (proposal.startRaw ?? ""),
    endRaw: proposal.endRaw ?? "",
    ongoing: proposal.ongoing,
    note: proposal.note ?? "",
    visibility: "private",
    viewers: [],
  };
}

function PendingProposals() {
  const queryClient = useQueryClient();
  const proposals = useQuery({
    queryKey: queryKeys.proposals,
    queryFn: fetchProposals,
    retry: false,
  });
  const frise = useQuery({ queryKey: queryKeys.frise, queryFn: fetchFrise, retry: false });
  const [editing, setEditing] = useState<Proposal | null>(null);
  const [error, setError] = useState<ApiError | null>(null);

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.proposals });
    void queryClient.invalidateQueries({ queryKey: queryKeys.frise });
    void queryClient.invalidateQueries({ queryKey: queryKeys.members });
  };
  const accept = useMutation({
    mutationFn: (id: number) => acceptProposal(id),
    onSuccess: refresh,
    onError: (thrown) => setError(asError(thrown)),
  });
  const refuse = useMutation({
    mutationFn: (id: number) => refuseProposal(id),
    onSuccess: refresh,
    onError: (thrown) => setError(asError(thrown)),
  });

  const list = proposals.data?.proposals ?? [];
  const tracks = frise.data?.tracks ?? [];
  const busy = accept.isPending || refuse.isPending;

  return (
    <section aria-labelledby="a-valider" className="flex flex-col gap-2.5">
      <h2 className="m-0 flex items-center gap-1 text-[15px] font-semibold" id="a-valider">
        À valider
        <span className="ml-1 inline-flex h-[22px] min-w-[22px] items-center justify-center rounded-full bg-primary px-1.5 text-xs text-primary-foreground">
          {list.length}
        </span>
      </h2>
      {proposals.isError ? (
        <p className="m-0 text-sm text-destructive" role="alert">
          Les propositions ne répondent pas pour le moment.
        </p>
      ) : null}
      {proposals.isSuccess && list.length === 0 ? (
        <p className="m-0 text-sm text-muted-foreground">Aucune proposition en attente.</p>
      ) : null}
      {error !== null ? (
        <p className="m-0 text-sm text-destructive" role="alert">
          {error.message}
        </p>
      ) : null}
      {list.map((proposal) => (
        <ProposalCard
          busy={busy}
          key={proposal.id}
          onAccept={() => {
            setError(null);
            accept.mutate(proposal.id);
          }}
          onEdit={() => setEditing(proposal)}
          onRefuse={() => {
            setError(null);
            refuse.mutate(proposal.id);
          }}
          proposal={proposal}
          track={tracks.find((track) => track.id === proposal.trackId)}
        />
      ))}
      {editing !== null ? (
        <EntrySheet
          entry={null}
          heading="Modifier puis ajouter"
          initial={formOfProposal(editing)}
          onClose={() => setEditing(null)}
          onSaved={refresh}
          submit={(body: CreateEntryInput) => acceptProposal(editing.id, { entry: body })}
          tracks={tracks}
        />
      ) : null}
    </section>
  );
}

function ProposalCard({
  proposal,
  track,
  busy,
  onAccept,
  onEdit,
  onRefuse,
}: {
  proposal: Proposal;
  track: Track | undefined;
  busy: boolean;
  onAccept: () => void;
  onEdit: () => void;
  onRefuse: () => void;
}) {
  return (
    <article
      aria-label={proposal.title}
      className="flex flex-col gap-2.5 rounded-xl border border-border bg-card p-3.5"
    >
      <span className="flex items-center gap-2 text-xs text-[#C9D0DE]">
        <span
          aria-hidden="true"
          className="size-2 rounded-full"
          style={{ background: track?.color ?? "#AEB6C8" }}
        />
        {track?.name ?? "Sans piste"} · proposé par {proposal.memberEmail}
      </span>
      <strong className="text-base">{proposal.title}</strong>
      <span className="text-sm text-[#C9D0DE]">
        {whenLabel(proposal)}
        {proposal.note ? ` · « ${proposal.note} »` : ""}
      </span>
      <div className="grid grid-cols-3 gap-2">
        <button
          className="min-h-11 rounded-[10px] bg-primary text-sm font-semibold text-primary-foreground disabled:opacity-60"
          disabled={busy}
          onClick={onAccept}
          type="button"
        >
          Ajouter
        </button>
        <button
          className="min-h-11 rounded-[10px] border border-input text-sm"
          disabled={busy}
          onClick={onEdit}
          type="button"
        >
          Modifier
        </button>
        <button
          className="min-h-11 rounded-[10px] border border-input text-sm disabled:opacity-60"
          disabled={busy}
          onClick={onRefuse}
          type="button"
        >
          Refuser
        </button>
      </div>
      <span className="text-xs text-muted-foreground">
        Ajouté, il reste visible de toi seul jusqu'à ce que tu l'ouvres.
      </span>
    </article>
  );
}

function InviteForm() {
  const queryClient = useQueryClient();
  const id = useId();
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<InvitedRole>("family");
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const invite = useMutation({
    mutationFn: () => inviteMember(email.trim(), role),
    onSuccess: (member) => {
      setDone(
        `Invitation enregistrée pour ${member.email}. Préviens cette personne : elle demandera son lien.`,
      );
      setEmail("");
      void queryClient.invalidateQueries({ queryKey: queryKeys.members });
    },
    onError: (thrown) => setError(asError(thrown).message),
  });
  const submit = (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setDone(null);
    if (email.trim() === "") {
      setError("Donne son adresse e-mail.");
      return;
    }
    invite.mutate();
  };
  return (
    <section aria-labelledby={`${id}-titre`} className="flex flex-col gap-2.5">
      <h2 className="m-0 text-[15px] font-semibold" id={`${id}-titre`}>
        Inviter quelqu'un
      </h2>
      <form className="flex flex-col gap-2.5" noValidate onSubmit={submit}>
        <label className="text-[13px] text-[#C9D0DE]" htmlFor={`${id}-mail`}>
          Son adresse e-mail
        </label>
        <input
          autoComplete="off"
          className={FIELD}
          id={`${id}-mail`}
          onChange={(event) => setEmail(event.target.value)}
          placeholder="prenom@exemple.fr"
          type="email"
          value={email}
        />
        <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-2">
          <label className="flex flex-col gap-1 text-[13px] text-[#C9D0DE]">
            Cercle
            <select
              className={FIELD}
              onChange={(event) => setRole(event.target.value as InvitedRole)}
              value={role}
            >
              <option value="family">{ROLE_NAMES.family}</option>
              <option value="close">{ROLE_NAMES.close}</option>
            </select>
          </label>
          <button
            className="min-h-11 self-end rounded-[10px] bg-primary px-4 text-[15px] font-semibold text-primary-foreground disabled:opacity-60"
            disabled={invite.isPending}
            type="submit"
          >
            Inviter
          </button>
        </div>
      </form>
      {error !== null ? (
        <p className="m-0 text-sm text-destructive" role="alert">
          {error}
        </p>
      ) : null}
      {done !== null ? (
        <p className="m-0 text-sm" role="status">
          {done}
        </p>
      ) : null}
      <p className="m-0 text-xs text-muted-foreground">
        L'app n'envoie rien : préviens-la toi-même, elle demandera son lien sur
        timeline.petitlab.fr.
      </p>
    </section>
  );
}

/** « Famille · voit 14 points », ou « Famille · invitation en attente » avant la premiere connexion. */
export function memberMeta(member: Member): string {
  const circle = ROLE_NAMES[member.role];
  if (!member.hasLoggedIn) return `${circle} · invitation en attente`;
  const points = member.visibleCount <= 1 ? "point" : "points";
  return `${circle} · voit ${String(member.visibleCount)} ${points}`;
}

function MemberList({ members }: { members: readonly Member[] }) {
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const revoke = useMutation({
    mutationFn: (id: number) => revokeMember(id),
    onSuccess: () => {
      setConfirming(null);
      void queryClient.invalidateQueries({ queryKey: queryKeys.members });
      void queryClient.invalidateQueries({ queryKey: queryKeys.proposals });
    },
    onError: (thrown) => setError(asError(thrown).message),
  });
  const active = members.filter((member) => member.revokedAt === null);
  return (
    <section aria-labelledby="ils-voient" className="flex flex-col gap-1">
      <h2 className="m-0 mb-1.5 text-[15px] font-semibold" id="ils-voient">
        Ils voient ma frise
      </h2>
      {active.length === 0 ? (
        <p className="m-0 text-sm text-muted-foreground">Personne pour le moment.</p>
      ) : null}
      {error !== null ? (
        <p className="m-0 text-sm text-destructive" role="alert">
          {error}
        </p>
      ) : null}
      <ul className="m-0 flex list-none flex-col p-0">
        {active.map((member) => (
          <li
            className="flex min-h-12 flex-wrap items-center justify-between gap-2.5 border-b border-[rgba(243,238,227,0.10)] py-1.5"
            key={member.id}
          >
            <span className="flex min-w-0 flex-col">
              <span className="truncate text-[15px]">{member.email}</span>
              <span className="text-xs text-muted-foreground">{memberMeta(member)}</span>
            </span>
            {confirming === member.id ? (
              <span className="flex gap-2">
                <button
                  className="min-h-11 rounded-lg border border-destructive px-3 text-[13px] text-destructive disabled:opacity-60"
                  disabled={revoke.isPending}
                  onClick={() => {
                    setError(null);
                    revoke.mutate(member.id);
                  }}
                  type="button"
                >
                  Oui, retirer
                </button>
                <button
                  className="min-h-11 rounded-lg border border-input px-3 text-[13px]"
                  onClick={() => setConfirming(null)}
                  type="button"
                >
                  Annuler
                </button>
              </span>
            ) : (
              <button
                aria-label={`Retirer ${member.email}`}
                className="min-h-11 rounded-lg border border-input px-3 text-[13px]"
                onClick={() => setConfirming(member.id)}
                type="button"
              >
                Retirer
              </button>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}
